import { z } from 'zod';

import { organizationSchema } from '../organization';
import {
  CancelReason,
  Currency,
  SubscriptionPaymentCycle,
  organizationBillingSchema,
} from '../organization-billing';

//////////////////////////
// Get billing status
export const getBillingByOrgIdReqSchema = z.object({
  params: z.object({
    id: organizationSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type GetBillingByOrgIdReq = z.infer<typeof getBillingByOrgIdReqSchema>;

export const getBillingByOrgIdResSchema = {
  200: z.object({
    data: organizationBillingSchema,
  }),
};
export type GetBillingByOrgIdRes = z.infer<(typeof getBillingByOrgIdResSchema)[200]>;

//////////////////////////
// Get plans data
export const getPlansDataReqSchema = z.object({
  params: z.object({}).nullish(),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type GetPlansDataReq = z.infer<typeof getPlansDataReqSchema>;

export const getPlansDataResSchema = {
  200: z.object({
    data: z.object({
      currency: z.nativeEnum(Currency),
      plans: z.array(
        z
          .object({
            id: z.string(),
            name: z.string(),
            originalMonthlyPrice: z.number(),
            monthlyPrice: z.number(),
            paymentCycle: z.nativeEnum(SubscriptionPaymentCycle),
          })
          .strip()
      ),
    }),
  }),
};
export type GetPlansDataRes = z.infer<(typeof getPlansDataResSchema)[200]>;

//////////////////////////
// Cancel subscription
// Cancel subscription
export const cancelSubscriptionReqSchema = z.object({
  params: z.object({
    organizationId: organizationSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z
    .object({
      reason: z.nativeEnum(CancelReason),
      extraDetails: z.string(),
    })
    .nullish(),
});
export type CancelSubscriptionReq = z.infer<typeof cancelSubscriptionReqSchema>;

export const cancelSubscriptionResSchema = {
  200: z.object({ ok: z.boolean() }),
};
export type CancelSubscriptionRes = z.infer<(typeof cancelSubscriptionResSchema)[200]>;

// Uncancel subscription
export const uncancelSubscriptionReqSchema = z.object({
  params: z.object({
    organizationId: organizationSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type UncancelSubscriptionReq = z.infer<typeof uncancelSubscriptionReqSchema>;

export const uncancelSubscriptionResSchema = {
  200: z.object({
    data: z.object({}).nullish(),
    err: z.string().nullish(),
    success: z.boolean(),
  }),
};
export type UncancelSubscriptionRes = z.infer<(typeof uncancelSubscriptionResSchema)[200]>;

// Update subscription
export const updateReqSchema = z.object({
  params: z.object({
    organizationId: organizationSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z.object({
    planId: z.string().min(5),
    discountCoupon: z.string().nullish(),
  }),
});
export type UpdateReq = z.infer<typeof updateReqSchema>;

export const updateResSchema = {
  200: z.object({
    data: z.object({
      immediate: z.boolean(),
      effectiveAt: z.number().optional(),
      redirectUrl: z.string().optional(),
    }),
  }),
};
export type UpdateRes = z.infer<(typeof updateResSchema)[200]>;

//////////////////////////
// Abort pending downgrade
export const abortDowngradeReqSchema = z.object({
  params: z.object({
    organizationId: organizationSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type AbortDowngradeReq = z.infer<typeof abortDowngradeReqSchema>;

export const abortDowngradeResSchema = {
  200: z.object({ ok: z.boolean() }),
};
export type AbortDowngradeRes = z.infer<(typeof abortDowngradeResSchema)[200]>;

//////////////////////////
// Subscribe subscription
export const subscribeSubscriptionReqSchema = z.object({
  params: z.object({ organizationId: organizationSchema.shape._id }),
  query: z.object({}).nullish(),
  body: z.object({
    planId: z.string(),
    toltReferral: z.string().nullish(),
  }),
});
export type SubscribeSubscriptionReq = z.infer<typeof subscribeSubscriptionReqSchema>;

export const subscribeSubscriptionResSchema = {
  200: z.object({
    data: z.object({
      checkout: z.string(),
    }),
  }),
};
export type SubscribeSubscriptionRes = z.infer<(typeof subscribeSubscriptionResSchema)[200]>;

//////////////////////////
// Request billing portal url
export const requestBillingPortalUrlReqSchema = z.object({
  params: z.object({ organizationId: organizationSchema.shape._id }),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type RequestBillingPortalUrlReq = z.infer<typeof requestBillingPortalUrlReqSchema>;

export const requestBillingPortalUrlResSchema = {
  200: z.object({
    url: z.string(),
    id: z.string(),
  }),
};
export type RequestBillingPortalUrlRes = z.infer<(typeof requestBillingPortalUrlResSchema)[200]>;
